import type { EmailTemplateTypeModel } from '@/typings/api/emailCenter';
import { F, getIntlTranslation } from '@/utils/intl';
import type { FormInstance } from 'antd';
import {
  ConfigProvider,
  Form,
  Input,
  Select,
  Switch,
  TimePicker,
  Tooltip,
} from 'antd';
import React from 'react';
import { useIntl } from 'umi';
import type { EmailVariableGroup } from '../../components/EmailBodyEditor';
import InputWithPartnerTranslation from '../../components/InputWithPartnerTranslation';
import styles from './TemplateDrawer.less';
import { KIND, SENDING_TIME_FORMAT } from './templateFormShape';

export interface EmailSettingsFieldsProps {
  form: FormInstance;
  readOnly: boolean;
  isEdit: boolean;
  /** Marketing create: the one case with no trigger to name the template after. */
  creatingMarketing: boolean;
  /** Step 1 of creating, which shows the bare fields without the groupings. */
  onCreateSettings: boolean;
  templateTypes: EmailTemplateTypeModel[];
  /** Events that already send an email — offered disabled, with the reason. */
  coveredEventTypes: Set<string>;
  /** The resolved-value strip, rendered only for a *mixed* subject/pretext. */
  mixedSubjectHint: React.ReactNode;
  mixedPretextHint: React.ReactNode;
  previewLanguage: string;
  templateName: string;
  /** `kind` as the form currently holds it — delivery is marketing-only. */
  kind: unknown;
  /** The sender-override opt-in, which gates the two sender fields. */
  senderOverride: unknown;
  variableGroups: EmailVariableGroup[];
  /** An NL2Go template: its references are not ours, so none are offered. */
  variablesUnsupported?: boolean;
  /** The journey's sender, shown as the placeholder for "keep the journey's". */
  journeyEmailConfig?: { from_name?: string; from_email?: string } | null;
}

/**
 * The template's own attributes, rendered *inside* the editor rather than in a
 * form above it, so the drawer is editor-only (plan: email-editor-unified, A2).
 *
 * While creating they are the whole of step 1 instead, mounted in the create
 * panel — one place at a time, or two `Form.Item`s would share each name.
 *
 * They stay `Form.Item`s of the drawer's `Form`: this component renders inside
 * that tree and reads it through context, so relocating the DOM is all that
 * happens. Moving them out for real would mean re-implementing `validateFields`,
 * the existing rules and the body validator that blocks a broken save.
 *
 * **Render it through a memoized element, never as `<EmailSettingsFields />` in
 * place.** `VisualEditorBody` is `React.memo` and takes this as `settingsSlot`;
 * a fresh element on each render is a changed prop, and the drawer re-renders on
 * every keystroke (`useStoreWatch` on name/subject/pretext/body). Typing on the
 * canvas would re-render the whole three-pane editor — palette, canvas,
 * inspector, each individually memoized and each bypassed from above.
 */
const EmailSettingsFields: React.FC<EmailSettingsFieldsProps> = ({
  form,
  readOnly,
  isEdit,
  creatingMarketing,
  onCreateSettings,
  templateTypes,
  coveredEventTypes,
  mixedSubjectHint,
  mixedPretextHint,
  previewLanguage,
  templateName,
  kind,
  senderOverride,
  variableGroups,
  variablesUnsupported,
  journeyEmailConfig,
}) => {
  const intl = useIntl();

  return (
    <ConfigProvider componentDisabled={readOnly}>
      {/**
       * Name, type and event: **shown when editing, never when creating.**
       *
       * Creating always starts from somewhere that already answers them. The
       * trigger row's Erstellen fixes the type *and* the event — re-asking is
       * asking the user to restate a choice they made by clicking, and lets
       * them contradict it. Marketing's create button fixes the type too; only
       * its name is genuinely unknown, so that one field is offered on its own
       * below.
       *
       * They stay editable afterwards: renaming, or re-pointing a template at
       * a different event, are real things to want later — just not decisions
       * to be made twice in the same minute.
       */}
      {isEdit && (
        <Form.Item
          name="name"
          label={<F id="common.attribute.name" defaultMessage="Name" />}
          rules={[{ required: true }, { max: 255 }]}
        >
          <Input />
        </Form.Item>
      )}

      {/* Marketing has no trigger to name it after, so it needs one. */}
      {!isEdit && creatingMarketing && (
        <Form.Item
          name="name"
          label={<F id="common.attribute.name" defaultMessage="Name" />}
          rules={[{ required: true }, { max: 255 }]}
        >
          <Input
            placeholder={getIntlTranslation(intl, {
              id: 'pages.emailCenter.templates.namePlaceholder',
              defaultMessage: 'e.g. Spring kitchen campaign',
            })}
          />
        </Form.Item>
      )}

      {isEdit && (
        <>
          {/**
           * The kind is fixed by the way the template came into being — the
           * trigger row's create button, or marketing's — and it is not a
           * choice to revisit: flipping it would re-point the template at a
           * different kind of send altogether. So it is not offered.
           *
           * The field stays registered, only hidden: the submit shape and the
           * settings further down (event, delivery) still read `kind`, and
           * `CREATE_STEP_FIELDS` validates it.
           */}
          <Form.Item name="kind" initialValue={KIND.TRANSACTIONAL} hidden>
            <Input />
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prev, current) => prev.kind !== current.kind}
          >
            {({ getFieldValue }) =>
              getFieldValue('kind') === KIND.MARKETING ? null : (
                <Form.Item
                  name="email_type"
                  label={
                    <F
                      id="pages.emailCenter.templates.eventType"
                      defaultMessage="Event"
                    />
                  }
                  rules={[{ required: true }]}
                >
                  <Select
                    options={templateTypes.map((templateType) => ({
                      value: templateType.key,
                      label: coveredEventTypes.has(templateType.key)
                        ? `${templateType.label} — ${getIntlTranslation(intl, {
                            id: 'pages.emailCenter.templates.eventCovered',
                            defaultMessage: 'already has a template',
                          })}`
                        : templateType.label,
                      description: templateType.description,
                      disabled: coveredEventTypes.has(templateType.key),
                    }))}
                    optionRender={(option) => (
                      <Tooltip title={option.data.description} placement="left">
                        <div>{option.label}</div>
                      </Tooltip>
                    )}
                  />
                </Form.Item>
              )
            }
          </Form.Item>
        </>
      )}

      {/* Creating shows the bare fields (per the create-screen spec); the
        grouping headline only earns its place among the editor's settings. */}
      {!onCreateSettings && (
        <div className={styles.sectionTitle}>
          <F
            id="pages.emailCenter.templates.section.inbox"
            defaultMessage="What recipients see in their inbox"
          />
        </div>
      )}

      {/* The language icon in front of the field is the whole translation
          affordance now (C1/C4): it replaces the label's insert-picker *and* the
          resolved-value strip for a field whose entire value is one key. The strip
          stays for a *mixed* value like `{{ customer.firstname }}, …`, which the
          binding model deliberately does not treat as bound. */}
      <Form.Item
        name="subject"
        label={
          <F
            id="pages.emailCenter.templates.subject"
            defaultMessage="Subject"
          />
        }
        extra={mixedSubjectHint}
        rules={[{ required: true }, { max: 255 }]}
      >
        <InputWithPartnerTranslation
          language={previewLanguage}
          templateName={templateName}
          fieldName={getIntlTranslation(intl, {
            id: 'pages.emailCenter.templates.subject',
            defaultMessage: 'Subject',
          })}
          placeholder={getIntlTranslation(intl, {
            id: 'pages.emailCenter.templates.subjectPlaceholder',
            defaultMessage: 'Enter subject',
          })}
          variableGroups={variableGroups}
          variablesUnsupported={variablesUnsupported}
          disabled={readOnly}
        />
      </Form.Item>

      <Form.Item
        name="pretext"
        label={
          <F
            id="pages.emailCenter.templates.pretext"
            defaultMessage="Pretext"
          />
        }
        tooltip={
          <F
            id="pages.emailCenter.templates.pretextHint"
            defaultMessage="Shown by email clients next to the subject line."
          />
        }
        extra={mixedPretextHint}
        rules={[{ max: 500 }]}
      >
        <InputWithPartnerTranslation
          language={previewLanguage}
          templateName={templateName}
          fieldName={getIntlTranslation(intl, {
            id: 'pages.emailCenter.templates.pretext',
            defaultMessage: 'Pretext',
          })}
          placeholder={getIntlTranslation(intl, {
            id: 'pages.emailCenter.templates.pretextPlaceholder',
            defaultMessage: 'Enter pretext',
          })}
          variableGroups={variableGroups}
          variablesUnsupported={variablesUnsupported}
          disabled={readOnly}
          rows={2}
        />
      </Form.Item>

      {/* Sender overrides and delivery timing are tuning, not a create
        decision — the create screen stays at journey, subject and pretext;
        these remain reachable in the editor's settings afterwards. */}
      {!onCreateSettings && (
        <>
          <div className={styles.sectionTitle}>
            <F
              id="pages.emailCenter.templates.section.sender"
              defaultMessage="Sender information"
            />
          </div>

          {/**
           * A section with an opt-in, not a collapsible.
           *
           * Every email already has a sender — the journey's — so these fields
           * are never "the sender", they are a deliberate exception to it. Behind
           * a disclosure they read as settings someone forgot to fill in, and
           * typing in one silently re-pointed the From of a single email. The
           * toggle states the exception first; off, the fields are not there at
           * all and the email simply sends as the journey does.
           */}
          <Form.Item
            name="sender_override"
            label={
              <F
                id="pages.emailCenter.templates.senderOverride"
                defaultMessage="Overwrite sender for this email"
              />
            }
            valuePropName="checked"
          >
            <Switch
              onChange={(checked) => {
                // The fields unmount, but rc-field-form keeps what was in them —
                // so an override switched off has to be cleared here, or the save
                // would still carry it.
                if (!checked) {
                  form.setFieldsValue({
                    sender_from_name: null,
                    sender_from_email: null,
                  });
                }
              }}
            />
          </Form.Item>

          {senderOverride && (
            <>
              <Form.Item
                name="sender_from_name"
                label={
                  <F
                    id="pages.emailCenter.templates.senderFromName"
                    defaultMessage="Sender name"
                  />
                }
                rules={[{ max: 255 }]}
              >
                {/* Empty means "keep the journey's" — the placeholder says which
                  sender that is, rather than leaving a blank field to guess at. */}
                <Input
                  placeholder={journeyEmailConfig?.from_name || undefined}
                />
              </Form.Item>
              <Form.Item
                name="sender_from_email"
                label={
                  <F
                    id="pages.emailCenter.templates.senderFromEmail"
                    defaultMessage="Sender email"
                  />
                }
                rules={[{ type: 'email' }]}
              >
                <Input
                  placeholder={journeyEmailConfig?.from_email || undefined}
                />
              </Form.Item>
            </>
          )}

          {/* Only marketing sends on a schedule; a transactional email answers
            its trigger immediately, so a preferred time is meaningless there. */}
          {kind === KIND.MARKETING && (
            <>
              <div className={styles.sectionTitle}>
                <F
                  id="pages.emailCenter.templates.section.delivery"
                  defaultMessage="Delivery"
                />
              </div>
              <Form.Item
                name="sending_time"
                label={
                  <F
                    id="pages.emailCenter.templates.sendingTime"
                    defaultMessage="Preferred sending time"
                  />
                }
              >
                <TimePicker format={SENDING_TIME_FORMAT} minuteStep={15} />
              </Form.Item>
            </>
          )}
        </>
      )}
    </ConfigProvider>
  );
};

export default EmailSettingsFields;
